import { useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useProfile } from '../../hooks/useProfile'
import { useAlertBadges } from '../../contexts/AlertBadgeContext'
import { useLienClient } from '../../contexts/ClientAfficheContext'
import { getNavStructure, itemCouvre } from '../../lib/navigation'
import { adminBadgeFor, useSolutionAdminBadges } from '../../lib/solutionRegistry'
import type { IconName, NavSection, NavItem } from '../../lib/navigation'
import { NavIcon } from './NavIcon'
import { NavItem as NavItemComponent } from './NavItem'
import { Tooltip, TooltipTrigger, TooltipContent } from '../ui/tooltip'

interface SidebarProps {
  collapsed: boolean
  onToggleCollapse: () => void
  /**
   * Spec 160 (FR-016, FR-017) — faux sous 1 280 px : la largeur impose la colonne d'icônes, le bouton de repli n'a
   * plus d'effet et disparaît. La préférence de l'utilisateur reste mémorisée et revient au-delà.
   */
  peutBasculer?: boolean
}

const SEPARATOR = (
  <div style={{ height: 1, background: 'rgba(255,255,255,0.06)', margin: '4px 8px' }} />
)

/**
 * Mapping fallback : si un NavItem n'a pas d'icône explicite, on lui en attribue une
 * via son `id`. Utilisé en mode sidebar réduit où chaque item doit être identifiable par une icône.
 */
function iconForItemId(id: string): IconName {
  switch (id) {
    // Spec 133 — seuls les items du SOCLE figurent ici. Les items d'une solution portent leur
    // propre `icon` dans sa déclaration (`register.tsx`) : le socle n'a pas à connaître leurs
    // identifiants, et une solution neuve n'a pas à modifier ce fichier (FR-006).
    case 'tableau-de-bord': return 'grid'
    case 'alertes': return 'bell'
    case 'aide': return 'help'
    case 'portfolio': return 'briefcase'
    case 'parametrages': return 'gear'
    case 'admin': return 'shield'
    default: return 'grid'
  }
}

function SectionHeader({ section, open, onToggle }: {
  section: NavSection
  open: boolean
  onToggle: () => void
}) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        width: '100%',
        padding: '7px 14px',
        background: 'transparent',
        border: 'none',
        cursor: 'pointer',
        color: 'hsl(var(--sidebar-foreground-muted))',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-caption)',
        fontWeight: 'bold',
        transition: 'color 0.12s',
      }}
    >
      <NavIcon name={section.icon} size={12} />
      <span style={{ flex: 1, textAlign: 'left' }}>{section.label}</span>
      <svg
        width="9" height="9" viewBox="0 0 12 12" fill="none"
        stroke="currentColor" strokeWidth="1.8"
        style={{ transition: 'transform 0.2s', transform: open ? 'rotate(90deg)' : 'rotate(0deg)', flexShrink: 0 }}
      >
        <path d="M4 2l4 4-4 4"/>
      </svg>
    </button>
  )
}

function SectionItem({ item }: { item: NavItem }) {
  // Spec 160 — un lien du menu emporte le client affiché (`EntreeLien`) : ouvert dans un nouvel onglet, il le garde.
  if (item.disabled) {
    return (
      <div aria-disabled="true" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding: '6px 14px 6px 30px',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-body)',
        color: 'rgba(255,255,255,0.28)',
        cursor: 'default',
        userSelect: 'none',
      }}>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.label}
        </span>
        {item.badge && (
          <span style={{
            fontSize: 'var(--fs-caption)',
            fontWeight: 'bold',
            padding: '1px 5px',
            borderRadius: 9999,
            background: 'rgba(255,255,255,0.08)',
            color: 'rgba(255,255,255,0.35)',
            letterSpacing: '.04em',
            flexShrink: 0,
          }}>
            {item.badge}
          </span>
        )}
      </div>
    )
  }

  return (
    <EntreeLien item={item} padding="6px 14px 6px 30px">
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.label}
      </span>
      {item.badge && item.badgeType === 'alert' && (
        <span style={{
          fontSize: 'var(--fs-caption)',
          fontWeight: 'bold',
          padding: '1px 5px',
          borderRadius: 9999,
          background: 'hsl(var(--destructive))',
          color: 'hsl(var(--destructive-foreground))',
          flexShrink: 0,
        }}>
          {item.badge}
        </span>
      )}
    </EntreeLien>
  )
}

/**
 * Spec 161 (D-161-07) — lien d'une entrée du menu, actif sur TOUS les chemins qu'elle couvre : le sien, ceux de ses
 * onglets et leurs sous-chemins (`itemCouvre`). `NavLink` ne connaît que son propre chemin : sur la page d'un onglet,
 * l'entrée ne se serait pas allumée. Le style est un objet (spec 160 : `TooltipTrigger
 * asChild` étale les styles, une fonction n'y laisse rien).
 */
function EntreeLien({ item, padding, children }: { item: NavItem; padding: string; children: ReactNode }) {
  const lien = useLienClient()
  const { pathname } = useLocation()
  const actif = itemCouvre(item, pathname)
  return (
    <Link
      to={lien(item.path)}
      aria-current={actif ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 7,
        padding,
        margin: '1px 4px',
        borderRadius: 3,
        textDecoration: 'none',
        fontFamily: 'var(--font-body)',
        fontSize: 'var(--fs-body)',
        color: actif ? 'hsl(var(--accent-foreground))' : 'hsl(var(--sidebar-foreground-soft))',
        background: actif ? 'hsl(var(--accent))' : 'transparent',
        transition: 'background 0.12s, color 0.12s',
        overflow: 'hidden',
      }}
    >
      {children}
    </Link>
  )
}

/**
 * Spec 161 (D-161-06) — entrée d'une section SANS titre (accueil : « Tableau de bord », « Alertes ») : au premier
 * niveau, avec son icône, comme le bas de menu, et sa pastille.
 */
function EntreeRacine({ item, badgeCount }: { item: NavItem; badgeCount: number }) {
  return (
    <EntreeLien item={item} padding="8px 14px">
      <NavIcon name={item.icon ?? iconForItemId(item.id)} size={14} />
      <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {item.label}
      </span>
      {badgeCount > 0 && (
        <span style={{
          fontSize: 'var(--fs-caption)', fontWeight: 'bold', padding: '1px 5px', borderRadius: 9999,
          background: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))', flexShrink: 0,
        }}>
          {badgeCount}
        </span>
      )}
    </EntreeLien>
  )
}

const compteurEntree = (item: NavItem, totalCritical: number, urgentRenewals: number, solutionBadges: Record<string, number>) =>
  item.id === 'alertes' ? totalCritical
  : item.id === 'renouvellements' ? urgentRenewals
  : adminBadgeFor(item.id, solutionBadges)

function CollapsibleSection({ section, totalCritical, urgentRenewals, solutionBadges, open, onToggle }: {
  section: NavSection
  totalCritical: number
  urgentRenewals: number
  /** Spec 150 — compteurs déclarés par les solutions sur leurs entrées d'administration. */
  solutionBadges: Record<string, number>
  /** Spec 160 — tenu par la barre latérale : la colonne d'icônes démonte les sections, l'état doit leur survivre. */
  open: boolean
  onToggle: () => void
}) {
  // Spec 161 — une section sans titre n'a ni en-tête ni repli : ses entrées sont toujours visibles, au premier niveau.
  if (!section.label) {
    return (
      <div style={{ paddingTop: 2, paddingBottom: 4 }}>
        {section.items.map(item => (
          <EntreeRacine key={item.id} item={item} badgeCount={compteurEntree(item, totalCritical, urgentRenewals, solutionBadges)} />
        ))}
      </div>
    )
  }
  return (
    <div>
      <SectionHeader section={section} open={open} onToggle={onToggle} />
      {open && (
        <div style={{ paddingBottom: 4 }}>
          {section.items.map(item => (
            <SectionItemWithBadge
              key={item.id}
              item={item}
              badgeCount={compteurEntree(item, totalCritical, urgentRenewals, solutionBadges)}
              badgeColor='hsl(var(--destructive))'
            />
          ))}
        </div>
      )}
    </div>
  )
}

function SectionItemWithBadge({ item, badgeCount, badgeColor }: {
  item: NavItem
  badgeCount: number
  badgeColor: string
}) {
  if (item.disabled) {
    return <SectionItem item={item} />
  }
  if (badgeCount > 0) {
    return (
      <EntreeLien item={item} padding="6px 14px 6px 30px">
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {item.label}
        </span>
        <span style={{
          fontSize: 'var(--fs-caption)', fontWeight: 'bold', padding: '1px 5px', borderRadius: 9999,
          background: badgeColor, color: 'hsl(var(--destructive-foreground))', flexShrink: 0,
        }}>
          {badgeCount}
        </span>
      </EntreeLien>
    )
  }
  return <SectionItem item={item} />
}

/**
 * NavItem en mode sidebar réduite — icône seule + tooltip Radix + dot rouge si badge.
 * Pour tout item qui porte un compteur (Alertes, Renouvellements, entrées d'administration) le tooltip est enrichi.
 */
function CollapsedNavItem({ item, sectionLabel, badgeCount }: {
  item: NavItem
  sectionLabel?: string
  badgeCount: number
}) {
  const lien = useLienClient()
  const icon = item.icon ?? iconForItemId(item.id)
  const hasBadge = badgeCount > 0
  // Spec 160 (FR-017) — toute entrée qui porte un compteur le montre dans son infobulle : replié, l'icône n'en a qu'un
  // point. Les compteurs d'administration déclarés par les solutions n'y étaient pas, alors que le menu déplié les chiffre.
  const isEnriched = hasBadge
  // Spec 160 (FR-017) — l'état actif se calcule ICI, et le style du lien est un objet : `TooltipTrigger asChild` fusionne
  // les styles par étalement, où un style en fonction (forme NavLink) ne laisse rien — l'entrée perdait son surlignage
  // et la boîte de son point d'alerte, affiché alors en haut du menu. Même règle de correspondance que NavLink.
  // Spec 161 — et sur tous les chemins que l'entrée couvre, onglets compris (`itemCouvre`).
  const actif = itemCouvre(item, useLocation().pathname)

  if (item.disabled) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <div aria-disabled="true"
            style={{
              width: 44, height: 44, flexShrink: 0, display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.28)', borderRadius: 4, cursor: 'default',
            }}
          >
            <NavIcon name={icon} size={24} />
          </div>
        </TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    )
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Link
          to={lien(item.path)}
          // Spec 160 (FR-010, FR-017) — une icône seule n'a pas de nom ; l'infobulle n'en donne pas. Le nom dit aussi la
          // pastille d'alerte, que l'icône ne montre que par un point.
          aria-label={hasBadge ? `${item.label} (${badgeCount})` : item.label}
          // Spec 161 (revue à froid de l'incrément 1) — `aria-current` posé sur TOUS les chemins couverts, comme le menu
          // déplié : `NavLink` ne le posait que sur son propre chemin, l'entrée regroupée s'allumait sans être annoncée.
          aria-current={actif ? 'page' : undefined}
          style={{
            position: 'relative',
            width: 44, height: 44, flexShrink: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            color: actif ? 'hsl(var(--accent-foreground))' : 'hsl(var(--sidebar-foreground-soft))',
            background: actif ? 'hsl(var(--accent))' : 'transparent',
            borderRadius: 4, textDecoration: 'none',
            transition: 'background 0.12s, color 0.12s',
          }}
        >
          <NavIcon name={icon} size={24} />
          {hasBadge && (
            <span
              aria-hidden
              style={{
                position: 'absolute', top: 4, right: 6,
                width: 8, height: 8, borderRadius: '50%',
                background: 'hsl(var(--destructive))',
                border: '1.5px solid hsl(var(--sidebar))',
              }}
            />
          )}
        </Link>
      </TooltipTrigger>
      <TooltipContent side="right">
        {isEnriched ? (
          <div style={{ minWidth: 140 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, fontWeight: 600, fontSize: 'var(--fs-small)' }}>
              <span>{item.label}</span>
              <span style={{
                background: 'hsl(var(--destructive))', color: 'hsl(var(--destructive-foreground))',
                fontSize: 'var(--fs-caption)', fontWeight: 'bold', padding: '1px 6px', borderRadius: 9999,
              }}>
                {badgeCount}
              </span>
            </div>
            {sectionLabel && (
              <div style={{ fontSize: 'var(--fs-caption)', color: 'hsl(var(--primary-foreground) / 0.85)', marginTop: 2 }}>
                {sectionLabel}
              </div>
            )}
          </div>
        ) : (
          item.label
        )}
      </TooltipContent>
    </Tooltip>
  )
}

const PragmelisLogo = () => (
  <svg viewBox="0 0 100 120" width="22" height="26" style={{ flexShrink: 0 }}>
    <rect x="0" y="0" width="100" height="120" rx="4" fill="#1E2A4A"/>
    <rect x="0" y="115" width="100" height="5" fill="#2E5FA3"/>
    <rect x="22" y="14" width="13" height="93" rx="1.5" fill="#fff"/>
    <rect x="22" y="14" width="50" height="13" rx="1.5" fill="#fff"/>
    <rect x="22" y="54" width="36" height="13" rx="1.5" fill="#fff"/>
    <polygon points="38,54 66,67 78,14" fill="#fff"/>
  </svg>
)

export function Sidebar({ collapsed, onToggleCollapse, peutBasculer = true }: SidebarProps) {
  const { profile } = useProfile()
  const { badges, urgentRenewals } = useAlertBadges()
  // Spec 160 — le logo mène à l'accueil DU CLIENT de l'onglet (revue à froid : « / » ouvert dans un nouvel onglet
  // retombait sur le dernier client consulté du navigateur).
  const lienLogo = useLienClient()('/overview')
  // Spec 150 — appelé avant tout retour anticipé (règle des hooks) ; hors administrateur, les solutions ne lisent rien.
  const solutionBadges = useSolutionAdminBadges(profile?.role === 'Admin')
  // Spec 160 — sections ouvertes ou fermées par l'utilisateur, tenues ICI : sous 1 280 px la colonne d'icônes démonte
  // les sections, et un état tenu par elles revenait à son défaut au retour, refermant ce que l'utilisateur avait ouvert.
  const [sectionsOuvertes, setSectionsOuvertes] = useState<Record<string, boolean>>({})
  if (!profile) return null

  const sectionOuverte = (s: NavSection) => sectionsOuvertes[s.id] ?? s.defaultOpen ?? false
  const totalCritical = Object.values(badges).reduce((s, n) => s + n, 0)
  const { sections, bottomItems } = getNavStructure(profile.role, profile.resellerTier)
  const branding = profile.branding

  return (
    <aside style={{
      position: 'relative',
      width: collapsed ? 56 : 240,
      minWidth: collapsed ? 56 : 240,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      background: 'hsl(var(--sidebar))',
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
      transition: 'width 0.20s, min-width 0.20s',
    }}>
      {/* Bouton toggle — interne à la sidebar (top-right) pour ne pas être tronqué */}
      {peutBasculer && <button
        onClick={onToggleCollapse}
        title={collapsed ? 'Étendre la sidebar' : 'Réduire la sidebar'}
        aria-label={collapsed ? 'Étendre la sidebar' : 'Réduire la sidebar'}
        style={{
          position: 'absolute',
          top: 14,
          right: 6,
          width: 24,
          height: 24,
          borderRadius: '50%',
          background: 'hsl(var(--accent))',
          color: 'hsl(var(--accent-foreground))',
          border: '2px solid hsl(var(--sidebar))',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: 'var(--fs-small)',
          fontWeight: 'bold',
          padding: 0,
          zIndex: 20,
          transform: collapsed ? 'rotate(180deg)' : 'none',
          transition: 'transform 0.20s',
          lineHeight: 1,
        }}
      >
        ‹‹
      </button>}

      {/* Logo — lien accueil, aligné à gauche (espace réservé pour le bouton toggle à droite) */}
      <Link
        to={lienLogo}
        // Spec 160 — replié, le lien n'a que le logo : il lui faut un nom (revue à froid de l'incrément 2). Déplié, son
        // texte visible le nomme.
        aria-label={collapsed ? 'Tableau de bord' : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: collapsed ? 0 : 10,
          justifyContent: 'flex-start',
          padding: collapsed ? '13px 0 13px 8px' : '13px 16px',
          paddingRight: collapsed ? 32 : 36,
          textDecoration: 'none',
          borderBottom: '1px solid rgba(255,255,255,0.06)',
          flexShrink: 0,
        }}
      >
        {branding?.logoUrl
          ? <img src={branding.logoUrl} alt={branding.displayName} style={{ height: 26, maxWidth: collapsed ? 28 : 80, objectFit: 'contain', flexShrink: 0 }} />
          : <PragmelisLogo />
        }
        {!collapsed && (
          <div>
            <div style={{
              fontFamily: 'var(--font-heading)',
              fontSize: 'var(--fs-lead)',
              color: 'hsl(var(--sidebar-foreground))',
              fontWeight: 'normal',
              lineHeight: 1.25,
            }}>
              {branding?.displayName ?? 'FinOps 365'}
            </div>
            {!branding && (
              <div style={{
                fontFamily: 'var(--font-body)',
                fontSize: 'var(--fs-caption)',
                color: 'hsl(var(--sidebar-foreground-muted))',
                lineHeight: 1.3,
              }}>by Pragmelis</div>
            )}
          </div>
        )}
      </Link>

      {/* Navigation */}
      {collapsed ? (
        // Mode réduit : flex column centré, gap explicite pour espacement vertical garanti
        <nav
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 9,
            paddingTop: 14,
            paddingBottom: 14,
            overflowY: 'auto',
            overflowX: 'hidden',
          }}
        >
          {sections.flatMap((section, i) => [
            ...(i > 0
              ? [
                  <div
                    key={`sep-${section.id}`}
                    aria-hidden
                    style={{ width: 24, height: 1, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }}
                  />,
                ]
              : []),
            ...section.items.map(item => (
              <CollapsedNavItem
                key={item.id}
                item={item}
                sectionLabel={section.label}
                badgeCount={compteurEntree(item, totalCritical, urgentRenewals, solutionBadges)}
              />
            )),
          ])}
          {bottomItems.length > 0 && (
            <div
              key="sep-bottom"
              aria-hidden
              style={{ width: 24, height: 1, background: 'rgba(255,255,255,0.12)', flexShrink: 0 }}
            />
          )}
          {bottomItems.map(item => (
            <CollapsedNavItem key={item.id} item={item} badgeCount={0} />
          ))}
        </nav>
      ) : (
        // Mode étendu : sections collapsibles + items textuels
        <nav style={{ flex: 1, paddingTop: 6, paddingBottom: 6 }}>
          {sections.map((section, i) => (
            <div key={section.id}>
              {i > 0 && SEPARATOR}
              <CollapsibleSection
                section={section} totalCritical={totalCritical} urgentRenewals={urgentRenewals} solutionBadges={solutionBadges}
                open={sectionOuverte(section)}
                onToggle={() => setSectionsOuvertes(s => ({ ...s, [section.id]: !sectionOuverte(section) }))}
              />
            </div>
          ))}

          {bottomItems.length > 0 && SEPARATOR}
          {bottomItems.map(item => (
            <NavItemComponent key={item.id} item={item} />
          ))}
        </nav>
      )}

      {/* Footer tagline — masqué en mode réduit */}
      {!collapsed && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid rgba(255,255,255,0.06)',
          fontFamily: 'var(--font-heading)',
          fontStyle: 'italic',
          fontSize: 'var(--fs-caption)',
          color: 'hsl(var(--sidebar-slogan))',
          lineHeight: 1.55,
          flexShrink: 0,
        }}>
          {branding
            ? 'Powered by Pragmelis'
            : <span>Moins de gaspillage.<br/>Plus de valeur. Votre IT.</span>
          }
          {/* Spec 159 — la version exécutée (spec 093) a quitté le pied du menu pour le menu du profil (Header.tsx),
              même texte, même infobulle, tous profils : un tampon de build n'a pas à s'afficher en permanence au client. */}
        </div>
      )}
    </aside>
  )
}
